/**
 * Shared booking constants and pure helpers.
 *
 * These lived inside BookingModal.js as module-scope values. They were moved
 * here (unchanged) so the URL-driven /book page and the modal share ONE
 * definition — the rules below decide what a customer can book, and two
 * drifting copies would mean two different answers to the same question.
 */

// Per-unit choices per addon ID: each bottle or each photographer picks one.
//   9 = Champagne Bottle (switched off 2026-10-09 for the separate Dolce and
//   Brut bottles, which need no picker), 30 = combo pack: Dolce or Brut.
//   31 = Photographer (15 min), 32 = Videographer (15 min): Hidden or Not
//   hidden (owner 2026-10-09; up to 2, e.g. one of each). The Ride Along
//   services ride with the guests, so they have no hidden choice.
// The worker keeps only these values (booking-api buildAddonOptionsJson) and
// still reads the champagne_types rows stored before 2026-10-09.
const HIDDEN_CHOICES = ['Hidden', 'Not hidden'];
export const ADDON_OPTIONS_BY_ADDON = {
  9: ['Dolce', 'Brut'],
  30: ['Dolce', 'Brut'],
  31: HIDDEN_CHOICES,
  32: HIDDEN_CHOICES,
};

export const hasAddonOptions = (addonId) =>
  Object.prototype.hasOwnProperty.call(ADDON_OPTIONS_BY_ADDON, addonId);

export const isHiddenChoiceAddon = (addonId) => ADDON_OPTIONS_BY_ADDON[addonId] === HIDDEN_CHOICES;

// Pre-picked so ADDING one never silently blocks "Next"; the picker shows it and
// the guest can switch. Brut is our most-ordered champagne; "Not hidden" is how
// a photographer normally works, except on the Proposal Tour (a surprise): Hidden.
export const defaultAddonOption = (addonId, tourId) => {
  const opts = ADDON_OPTIONS_BY_ADDON[addonId] || [];
  if (opts.includes('Brut')) return 'Brut';
  if (!isHiddenChoiceAddon(addonId)) return opts[0] || '';
  return isProposalTour(tourId) ? 'Hidden' : 'Not hidden';
};

/** booking_addons.addon_options → "1 Hidden, 1 Not hidden" (booking-api stores the display); '' when none. */
export const addonOptionsDisplay = (json) => {
  try { return JSON.parse(json || 'null')?.display || ''; } catch { return ''; } // old or malformed row
};

/** What one unit is called next to its picker: "Bottle 2", "Photographer 1". */
export const addonOptionUnit = (addonId) => ({ 31: 'Photographer', 32: 'Videographer' }[addonId] || 'Bottle');

/** The line under the Hidden / Not hidden picker. */
export const hiddenChoiceHelp = (addonId) => {
  const who = addonOptionUnit(addonId).toLowerCase();
  return `Hidden: the ${who} works discreetly from a distance, good for a surprise proposal. Not hidden: the ${who} works openly with you.`;
};

// Email format gate. We only validated "non-empty" before, so a malformed
// address (no @, missing domain) sailed through the form and only failed
// downstream at Stripe's create_checkout_session — surfacing as a cryptic
// "payment failed" instead of a fixable field error. Validate shape here.
export const isValidEmail = (email) =>
  typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const RESERVATION_REQUIRED_NOTICE = 'Reservations are required. No walk-up service at the carriage stand.';

// ────────────────────────────────────────────────────────────────────────────
// Christmas tour helpers
// ────────────────────────────────────────────────────────────────────────────
//
// All Christmas variant tour IDs (D1):
//   29 = Park Only      ($240) — daytime + evenings, all hours (Central Park Christmas Ride)
//   37 = Park + Avenue ($240) — evenings 7 PM onward (Holiday Avenue Ride)
//   38 = Avenue Only    ($320) — late night 8:40 PM onward
// 19 is the legacy HOLIDAY VIP tour kept for backward compatibility (old links).
export const CHRISTMAS_TOUR_IDS = [19, 29, 37, 38];

// Christmas season window — the Christmas booking calendar is locked to these
// dates (no bookings outside the season). Update both ends when it shifts.
export const CHRISTMAS_SEASON_START = new Date(2026, 10, 21); // Nov 21, 2026
export const CHRISTMAS_SEASON_END = new Date(2027, 0, 9);     // Jan 9, 2027

/**
 * Is "YYYY-MM-DD" inside the Christmas season (Nov 21 → Jan 9, inclusive)?
 *
 * Month/day rule, any year — the concrete CHRISTMAS_SEASON_START/END Dates
 * above still clamp the Christmas calendar, but the "regular tours pause"
 * rule must not silently expire when those two constants fall behind. Used by
 * the booking calendar and by the worker (which bundles this file).
 */
export const isChristmasSeasonDate = (dateString) => {
  const m = String(dateString || '').match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const month = Number(m[1]);
  const day = Number(m[2]);
  return (month === 11 && day >= 21) || month === 12 || (month === 1 && day <= 9);
};

/** A date in THIS season, the one the Christmas rides sell now; a later year's December is not. */
export const isThisChristmasSeason = (date) => {
  const day = toDateString(date);
  return day >= toDateString(CHRISTMAS_SEASON_START) && day <= toDateString(CHRISTMAS_SEASON_END);
};

// Weekday holidays that still depart 8:30 like the weekend: Thanksgiving (the fourth
// Thursday of November) and New Year's Day, every year, not a list that runs out.
export const isChristmasEarlyStartDate = (dateString) => {
  const m = String(dateString || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const day = Number(m[3]);
  if (m[2] === '01') return day === 1;
  return m[2] === '11' && day >= 22 && day <= 28 && new Date(Date.UTC(Number(m[1]), 10, day)).getUTCDay() === 4;
};

// Tours whose route runs along city avenues — they get a "duration may vary
// with traffic" note. 37 = Park + Avenue, 38 = Avenue.
export const AVENUE_TOUR_IDS = [37, 38];

// Rockefeller Royal Lights Ride — night-only tour (8 PM – 11:30 PM).
export const ROCKEFELLER_TOUR_ID = 25;

/**
 * Tours that only run after dark, as MINUTES past midnight.
 *
 * Minutes, not hours: the owner's windows are half-past times, and the old
 * hour-only form could not express 8:30 PM at all — it would have opened the
 * tour half an hour early, every night.
 *
 * Departure windows are decided here, not by the tours.time_range column —
 * that field is display-only (the tours page reads it and nothing else). A new
 * "evening" tour with no entry here is bookable from 8 or 10 in the MORNING,
 * which is how it would reach a customer.
 */
export const EVENING_TOUR_START_MINUTES = {
  47: 20 * 60 + 30, // Royal Midtown Lights Tour — 8:30 PM
  49: 19 * 60 + 30, // Royal Park & City Grand Tour — 7:30 PM
  50: 20 * 60 + 30, // Royal Manhattan Evening Tour — 8:30 PM
};

/** Nothing may depart late enough to still be out after this hour. */
export const LAST_RETURN_HOUR = 23;

// Photo/video add-ons, shown together (in this order) under "Photo & video
// coverage". Any combination may be booked (owner 2026-10-09), each up to its
// max_quantity. Live D1 ids (No Worries Services Co. catalogue):
//   31 = 📸 Photographer (15 min), 32 = 🎥 Videographer (15 min),
//   35 = 📸 Ride Along Photo (up to 1 hour), 71 = 🎞️ Ride Along Video (up to 1 hour).
export const PHOTO_VIDEO_ADDON_IDS = [31, 32, 35, 71];

/** Matches the add-on names No Worries Services Co. fulfils (photo/video). */
export const NO_WORRIES_PHOTO_PATTERN = /photograph|videograph|ride along/i;

/** The one Christmas day the Park ride (29) starts at 2:30 PM; Park + Avenue (37) has the morning. */
export const CHRISTMAS_PARK_LATE_START_DATE = '2026-12-05';

/**
 * Decide which Christmas variants are available at a given clock time
 * (minutes since midnight) and date string ("YYYY-MM-DD"). Returns an ordered
 * array of tour IDs.
 *
 * Standard Christmas dates:
 *   8:30 AM – 4:45 PM (45-min daytime):           [29]            (Park Only)
 *   7:00 PM (1140), 7:50 PM (1190):               [37, 29]        (Park + Avenue + Park Only)
 *   8:40, 9:30, 10:20, 11:10 PM:                  [37, 29, 38]    (+ Avenue Only)
 *   any other minute:                             []
 *
 * Dec 5, 2026 exception — the Park is closed until 2 PM, so Park Only cannot
 * run that morning; Park + Avenue covers it, then the standard rule resumes.
 */
export const variantsAtChristmasTime = (totalMinutes, dateString) => {
  const isDec5_2026 = dateString === CHRISTMAS_PARK_LATE_START_DATE;
  if (totalMinutes >= 510 && totalMinutes <= 1005) {
    // 8:30 AM (510) – 4:45 PM (16*60+45=1005). 45-min cadence.
    if ((totalMinutes - 510) % 45 !== 0) return [];
    if (isDec5_2026) {
      // Last morning Park+Avenue (fallback) slot: 1:45 PM = 825.
      // First afternoon Park Only slot: 2:30 PM = 870.
      return totalMinutes <= 825 ? [37] : [29];
    }
    return [29]; // Daytime = Central Park Christmas Ride (Park Only)
  }
  if (totalMinutes === 1140 || totalMinutes === 1190) return [37, 29];
  if ([1240, 1290, 1340, 1390].includes(totalMinutes)) return [37, 29, 38];
  return [];
};

/** Parse a UI slot label like "8:40 PM" → minutes since midnight. -1 if unparseable. */
export const parseSlotLabelToMinutes = (label) => {
  if (!label) return -1;
  const m = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return -1;
  let hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && hour < 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  return hour * 60 + minute;
};

/**
 * Format minutes-since-midnight back into the UI label ("8:40 PM").
 * Inverse of parseSlotLabelToMinutes.
 */
export const formatMinutesToSlotLabel = (totalMinutes) => {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

/**
 * Convert a slot label ("8:40 PM") to the 24-hour "HH:MM" key used by
 * `time_slot_capacity.time_slot`. Bookings are stored with the LABEL, so any
 * live capacity lookup has to normalise through this — otherwise the counts
 * silently never match.
 */
export const slotLabelToTimeKey = (label) => {
  const mins = parseSlotLabelToMinutes(label);
  if (mins < 0) return null;
  const h = String(Math.floor(mins / 60)).padStart(2, '0');
  const m = String(mins % 60).padStart(2, '0');
  return `${h}:${m}`;
};

/**
 * Valentine's Day is no longer a separate tour. Every tour runs that day and
 * chocolate comes with it, so this is the one place that phrasing lives —
 * booking summary and date step both read it.
 */
export const VALENTINES_CHOCOLATE_NOTE =
  'Complimentary chocolate is included with all tours on Valentine’s Day.';

/** True when a Date (or "YYYY-MM-DD") falls on February 14, any year. */
export const isValentinesDay = (date) => {
  if (!date) return false;
  if (typeof date === 'string') {
    const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return !!m && m[2] === '02' && m[3] === '14';
  }
  return date.getMonth() === 1 && date.getDate() === 14;
};

/**
 * The next Valentine's Day that hasn't happened yet.
 *
 * Computed, never hardcoded: BookingModal pins `date: '2026-02-14'`, which is
 * already in the past, so anything reusing that constant silently lands on a
 * dead date. DatePicker restricts `valentineOnly` tours to any Feb 14, so this
 * is the matching "which one" answer.
 */
export const nextValentinesDay = (now = new Date()) => {
  const year = now.getFullYear();
  const thisYear = new Date(year, 1, 14);
  // Compare by day, not by time, so Feb 14 itself still counts as upcoming.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return thisYear >= today ? thisYear : new Date(year + 1, 1, 14);
};


/**
 * Proposal Tour (36) and the three spots the driver can stop at.
 *
 * The names must match exactly what booking-api stores in `proposal_location`
 * and what the driver sheet prints — they are not display labels, they are the
 * value. Kept here so /book, the /proposals page and the admin manual form all
 * offer the same three.
 */
export const PROPOSAL_TOUR_ID = 36;
export const PROPOSAL_LOCATIONS = ['Bethesda Terrace Arcade', 'Wagner Cove', 'Bow Bridge'];
export const isProposalTour = (tourId) => parseInt(tourId, 10) === PROPOSAL_TOUR_ID;

/**
 * Headline price and length of the Proposal Tour, for copy written BEFORE the
 * tours API answers (the /proposals hero is the first thing on screen and must
 * not wait on a fetch). The reservation section still shows the live row once
 * it loads; these two are the same numbers, kept in one place instead of the
 * three literals that were typed into ProposalReservation.
 */
export const PROPOSAL_TOUR_PRICE = 224.99;
export const PROPOSAL_TOUR_DURATION_MIN = 65;

// ────────────────────────────────────────────────────────────────────────────
// Where we are and how to reach us
// ────────────────────────────────────────────────────────────────────────────
//
// The phone number was hardcoded in five separate components and the address in
// three, which is how a business quietly ends up publishing two different
// numbers. One definition, used everywhere. The map links are the same ones the
// confirmation email sends, so the pin a guest taps before booking is the pin
// they get afterwards.
export const PHONE_DISPLAY = '(646) 656-9717';
export const PHONE_HREF = 'tel:+16466569717';

export const MEETING_POINT_STREET = '180 Central Park South';
export const MEETING_POINT_ADDRESS = '180 Central Park South, New York, NY 10019';

export const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/yjgGyrc18x6M73R77';
export const APPLE_MAPS_URL =
  'https://maps.apple.com/place?place-id=IF775DFB614BBFE7A&address=7th+Ave+%26+Central+Park+S%2C+New+York%2C+NY++10028%2C+United+States&coordinate=40.766935%2C-73.979051&name=NYC+Royal+Carriage&_provider=9902';

/** "YYYY-MM-DD" for a local Date (no UTC shift). */
export const toDateString = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ────────────────────────────────────────────────────────────────────────────
// Carriage capacity — ONE table
// ────────────────────────────────────────────────────────────────────────────
//
// The same seating rows were typed by hand in DateTimeStep, BookingModal and
// the Terms of Service, and the three had drifted (Terms stopped at "3 adults
// with 2 children"). Everything that talks about how many people fit reads
// this table now: the carriage picker, the booking summary, the Terms page
// and the FAQ copy.
export const GUESTS_PER_CARRIAGE = 4;
export const CHILD_MAX_AGE = 12;
export const CARRIAGE_SEATING = [
  { adults: 4, children: 0 },
  { adults: 3, children: 2 },
  { adults: 2, children: 4 },
  { adults: 1, children: 6 },
];

/** "3 adults & up to 2 children" / "4 adults" */
export const seatingLabel = ({ adults, children }) => {
  const a = `${adults} adult${adults === 1 ? '' : 's'}`;
  if (!children) return a;
  return `${a} & up to ${children} child${children === 1 ? '' : 'ren'}`;
};

/** Human sentence used by Terms/FAQ: "4 adults, 3 adults & up to 2 children, …" */
export const seatingSummary = () => CARRIAGE_SEATING.map(seatingLabel).join(', ');

// ────────────────────────────────────────────────────────────────────────────
// Tour prices — ONE static table
// ────────────────────────────────────────────────────────────────────────────
//
// The nine visible D1 tours, cheapest first (the order the live list sorts
// into). The live list from ToursContext replaces it on /tours as soon as the
// API answers; this copy is what the react-snap HTML carries if the API is down
// at build time, what a crawler that runs no JS reads, and what the press kit
// prints. It used to live inside ToursPage, so /press could not read it without
// pulling in the whole tours chunk — and a second hand-typed price list is how
// a business ends up publishing two prices for the same ride. Keep in step
// with D1.
export const TOUR_PRICE_TABLE = [
  { slug: 'central-park-short-horse-carriage-ride-nyc', name: 'Mini Tour', duration: '25 minutes', price: 104.99 },
  { slug: 'central-park-horse-carriage-ride-nyc-standard', name: 'Signature Tour', duration: '45 minutes', price: 164.99 },
  { slug: 'central-park-horse-carriage-ride-nyc-premium', name: 'Silver Tour', duration: '55 minutes', price: 194.99 },
  { slug: 'royal-midtown-lights-horse-carriage-ride', name: 'Royal Midtown Lights Tour', duration: '55 minutes', price: 194.99 },
  { slug: 'central-park-private-horse-carriage-ride-nyc', name: 'Royal Tour', duration: '65 minutes', price: 224.99 },
  { slug: 'central-park-night-horse-carriage-ride-nyc-rockefeller', name: 'Rockefeller Royal Lights Ride', duration: '65 minutes', price: 224.99 },
  { slug: 'central-park-royal-grand-horse-carriage-ride', name: 'Central Park Royal Grand Tour', duration: '125 minutes', price: 414.99 },
  { slug: 'royal-park-and-city-grand-horse-carriage-ride', name: 'Royal Park & City Grand Tour', duration: '125 minutes', price: 414.99 },
  { slug: 'royal-manhattan-evening-horse-carriage-ride', name: 'Royal Manhattan Evening Tour', duration: '125 minutes', price: 414.99 },
];

// ────────────────────────────────────────────────────────────────────────────
// Cancellation policy — ONE list
// ────────────────────────────────────────────────────────────────────────────
//
// Lifted verbatim from the Terms of Service so the pre-payment summary on
// /book, the Terms page and the Contact page all quote the same tiers. Edit
// here, never in a component.
export const CANCELLATION_POLICY_LINES = [
  { id: 'full', text: 'Cancellations made 24+ hours before the tour: full refund (excluding transaction fees)' },
  { id: 'half', text: 'Cancellations made 12–24 hours before the tour: 50% refund' },
  { id: 'none', text: 'Cancellations made less than 12 hours before the tour, and no-shows: no refund' },
  { id: 'reschedule', text: `Rescheduling: up to 12 hours before the scheduled time — call ${PHONE_DISPLAY}` },
  { id: 'weather', text: 'Weather-related cancellations by NYC Royal Carriage: full refund or free rescheduling' },
  { id: 'late', text: 'Late arrival: more than 15 minutes late without contacting us forfeits the refund and rescheduling options' },
];

/** The four lines worth showing before someone pays (the rest live in Terms). */
export const CANCELLATION_POLICY_SUMMARY_IDS = ['full', 'half', 'none', 'weather'];

// ────────────────────────────────────────────────────────────────────────────
// Routes where the global sales chrome must stay out of the way
// ────────────────────────────────────────────────────────────────────────────
//
// The sticky "Book Online / Call Us" bar and the WhatsApp bubble are mounted
// site-wide. On the booking flow itself they covered the Continue button and
// invited a guest who was already booking to "book" — so they hide here.
export const QUIET_CHROME_ROUTES = /^\/(book|booking-success|manage-booking|driver)(\/|$)/;
export const isQuietChromeRoute = (pathname) => QUIET_CHROME_ROUTES.test(pathname || '');
